import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useMemo } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { ArrowLeftOutline, Button, Display, HStack, Stack, useToast } from '@bonusly/design-system';

import { AdminPageContainer } from '../../../components/layout/admin-page-container';
import { PageTitle } from '../../../components/page/page-title';
import { extractBackendErrorMessage } from '../../../queries/meta';
import { useCreatePointExpiryPeriod } from '../../../queries/point-expiry';
import { POINT_EXPIRY_INDEX_PATH, toPayload } from '../point-expiry-form-helpers';
import { createPointExpiryFormSchema, DEFAULT_POINT_EXPIRY_FORM_VALUES, PointExpiryFormData } from '../schema';
import { PointExpiryForm } from './point-expiry-form';

export const CreatePointExpiryPage: FC = () => {
	const { t } = useTranslation('admin', { keyPrefix: 'pointExpiryPeriods.form' });
	const toast = useToast();
	const navigate = useNavigate();

	const formSchema = useMemo(() => createPointExpiryFormSchema(t), [t]);

	const form = useForm<PointExpiryFormData>({
		resolver: zodResolver(formSchema) as unknown as Resolver<PointExpiryFormData>,
		defaultValues: DEFAULT_POINT_EXPIRY_FORM_VALUES,
	});

	const { mutateAsync: createPeriod, isLoading } = useCreatePointExpiryPeriod();

	const handleSubmit = async (data: PointExpiryFormData) => {
		try {
			await createPeriod(toPayload(data));
			toast({ title: t('createSuccess'), status: 'success' });
			navigate(POINT_EXPIRY_INDEX_PATH);
		} catch (error) {
			toast({ title: extractBackendErrorMessage(error) ?? t('saveFailure'), status: 'error' });
		}
	};

	return (
		<>
			<PageTitle>{t('createHeader')}</PageTitle>

			<AdminPageContainer width="narrow">
				<Stack spacing={6}>
					<HStack>
						<Button as={Link} variant="link" gap={1} to={POINT_EXPIRY_INDEX_PATH} type="button">
							<ArrowLeftOutline size="xs" />
							{t('back')}
						</Button>
					</HStack>

					<Display size="sm">{t('createHeader')}</Display>

					<PointExpiryForm form={form} onSubmit={handleSubmit}>
						<HStack justify="flex-end" width="100%">
							<Button type="submit" isLoading={isLoading}>
								{t('createCta')}
							</Button>
						</HStack>
					</PointExpiryForm>
				</Stack>
			</AdminPageContainer>
		</>
	);
};
